/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';

import { IOverflowContainerProps } from './overflow-container.props.autogenerated';

export interface IOverflowContainerViewProps extends IOverflowContainerProps<{}> {
    content: React.ReactNode;
    btn: React.ReactNode;
    currentClass: string;
}

interface IOverflowContainerData {
    isOverflowExpandedState: boolean;
}

/**
 *
 * OverflowContainer component
 * @extends {React.PureComponent<IOverflowContainerProps<{}>, IOverflowContainerData>}
 */
class OverflowContainer extends React.PureComponent<IOverflowContainerProps<{}>, IOverflowContainerData> {
    private readonly _OPENED_CLASS: string = 'overflow-container-opened';
    private readonly _CLOSED_CLASS: string = 'overflow-container-closed';

    constructor(props: IOverflowContainerProps<{}>) {
        super(props);

        this.state = {
            isOverflowExpandedState: !!this.props.config.openByDefault
        };

        this._toggleExpansion = this._toggleExpansion.bind(this);
    }

    public componentDidMount(): void {
        if (this.props.config.closeOnClickOff) {
            document.body && document.body.addEventListener('mousedown', this._handleClickOutsideOverflow);
        }
    }

    public componentWillUnmount(): void {
        if (this.props.config.closeOnClickOff) {
            document.body && document.body.removeEventListener('mousedown', this._handleClickOutsideOverflow, false);
        }
    }

    public render(): JSX.Element | null {
        const props: IOverflowContainerViewProps = {
            ...this.props,
            content: this._jsxContent,
            btn: this._jsxBtn,
            currentClass: this._currentClass
        };
        return this.props.renderView(props) as JSX.Element;
    }

    private _toggleExpansion(): void {
        this.setState({
            isOverflowExpandedState: !this.state.isOverflowExpandedState
        });
    }

    // tslint:disable-next-line:no-any
    private _handleClickOutsideOverflow = (event: any) => {
        const overflow:boolean = event.target.className ===  'overflow-container-btn';
        const pulldownElement = document.getElementById('overflow-container-content');
        if (this.state.isOverflowExpandedState && !pulldownElement?.contains(event.target) && !overflow) {
            this.setState({
                isOverflowExpandedState: false
            });
        }
    };

    private get _jsxContent(): JSX.Element | null {
        const content = this.props.slots.content;

        return (
            <div className='overflow-container-content' id='overflow-container-content'>
                {content && content.map((module, index) => (
                    <React.Fragment key={index}>
                        {module}
                    </React.Fragment>
                ))}
            </div>
        );
    }

    private get _currentClass(): string {
        return (this.state.isOverflowExpandedState) ? this._OPENED_CLASS : this._CLOSED_CLASS;
    }

    private get _jsxBtn(): JSX.Element {
        const btn = this.props.config.btn;
        const btnText = (this.state.isOverflowExpandedState) ? btn?.openText || 'Close' : btn?.closeText || 'Expand';
        return (<button onClick={this._toggleExpansion} className='overflow-container-btn' aria-label={this.state.isOverflowExpandedState ? 'Click to Close' : 'Click to Expand'}>{btnText}</button>);
    }
}

export default OverflowContainer;
